import { useMantineTheme } from '@mantine/core';
import { notifications, showNotification } from '@mantine/notifications';
import { IconCheck, IconCircleX, IconX } from '@tabler/icons-react';
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../axios';
import { safeLocalStorage } from '../../utils/safeStorage';

// Create a Context
const AppContext = createContext();

// ScreenSizeProvider component
export const AppContextProvider = ({ children }) => {
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(false);
  const [user,setUser] = useState(null)
  const theme = useMantineTheme();


// In AppContextProvider component
useEffect(() => {
  // Only check on mount and when app becomes visible
  const checkAndRefreshToken = async () => {
    const token = safeLocalStorage.getItem('token');
    if (!token) return;

    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiryDate = new Date(payload.exp * 1000);
        const now = new Date();
        const hoursUntilExpiry = (expiryDate - now) / (1000 * 60 * 60);
        
        console.log(`Token expires in ${hoursUntilExpiry.toFixed(1)} hours`);
        
        // Refresh if less than 2 hours remaining OR already expired
        if (hoursUntilExpiry < 24) {
            console.log("Refreshing token...");
            const response = await axiosInstance.post('/api/user/refresh-token');
            if (response.data.success) {
                safeLocalStorage.setItem('token', response.data.data.token);
                console.log("Token refreshed successfully");
                navigate('/');
            }
        }
    } catch (error) {
        console.error('Token refresh check failed:', error);
    }
};
  
  checkAndRefreshToken(); // On mount
  
  const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
          checkAndRefreshToken(); // On app resume
      }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
}, []);

  // Function to update the screen size on window resize
  const updateScreenSize = () => {
    setScreenSize(window.innerWidth);
    setIsMobile(window.innerWidth <= 16 * parseFloat(theme.breakpoints.md));
  };

  useEffect(() => {
    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    console.log(isMobile)
    // Cleanup listener on component unmount
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const [queuedNavigation, setQueuedNavigation] = useState(null);

    const notify = (type, message,size,onCloseCallback=null) => {
      console.log(type, message);
      if (size === 'large') {
        // Custom notification for mobile
        const notificationCard = document.createElement('div');
        notificationCard.id = 'mobile-notification';
        document.body.appendChild(notificationCard);
      
        const notificationHTML = `
        <div style="
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(255, 255, 255, 0); // Slightly transparent white
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            backdrop-filter: blur(5px); // Add blur effect
          ">
          <div style="
            position: fixed;
            top: 40%;
            left: 50%;
            transform: translateX(-50%);
            z-index: 9999;
            background: #faf1ea;
            padding: 30px;
            box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.1);
            border-radius: 12px;
            width: 90%;
            max-width: 450px;
            font-size: 1rem;
            transition: opacity 0.5s ease-in-out;
            display: flex;
            flex-direction: column;
            align-items: center;
          ">
            <div style="border-radius: 50%; padding: 10px; background-color: ${type === 'success' ? 'green' : 'red'}; margin-bottom: 10px;">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                ${type === 'success' ? `
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="9" y1="12" x2="12" y2="15"></line>
                  <line x1="12" y1="15" x2="15" y2="9"></line>
                ` : `
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="16"></line>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                `}
              </svg>
            </div>
            <div style="font-weight: bold; font-size: 1.2rem; color: ${type === 'success' ? 'green' : 'red'}; margin-bottom: 8px;">
              ${type === 'success' ? 'Success!' : 'Error!'}
            </div>
            <div style="font-size: 1rem; color: #333;">
              ${message}
            </div>
          </div>
        </div> 
        `;
      
        notificationCard.innerHTML = notificationHTML;
      
        setTimeout(() => {
          notificationCard.style.opacity = 0;
        setTimeout(() => notificationCard.remove(), 500);
        console.log(onCloseCallback)
        if (onCloseCallback) onCloseCallback(); 
        }, 1500); // Notification disappears after 3 seconds
      } else {
        console.log("type, message");
        console.log(onCloseCallback);
        // Mantine notifications for desktop
        showNotification({
          title: type === 'success' ? 'Success' : 'Error',
          message,
          color: type === 'success' ? 'green' : 'red',
          icon: type === "success" ? (
            <IconCheck size={20} color="green" />
          ) : (
            <IconCircleX size={20} color="red" />
          ),
          onClose: onCloseCallback,
          autoClose: 2000,
        });
      }
    };

    const triggerNavigation = (navigate) => {
      if (queuedNavigation) {
        navigate(queuedNavigation);
        setQueuedNavigation(null);
      }
    };  

  return (
    <AppContext.Provider value={{screenSize,isMobile,user,setUser,notify}}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the AppContext
export const useAppContext = () => useContext(AppContext);
export const useScreenSize = () => {
  const { screenSize } = useAppContext();
  return screenSize;
}
export const useIsMobile = () => {
  const { isMobile } = useAppContext();
  return isMobile;
}
